/* eslint-disable react/jsx-no-literals */
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ApplicationIcon,
  Button,
  Chip,
  Icon,
  Tooltip,
} from '@saviynt/design-system';

import { AAG_LICENSE_INTELLIGENCE_BASENAME } from '../../../../../shared/constants';
import { LICENSE_INTELLIGENCE_SNAPSHOT_DATA } from '../../../mock-data/licenseIntelligenceSnapshotData';

import TITLE_SECTION_CONSTANTS, { getUtilizationThreshold } from './constants';

import './TitleSection.css';

interface TitleSectionProps {
  vendor?: string;
}

/**
 * TitleSection component for the License Details page.
 *
 * @description Renders breadcrumbs, vendor logo, license name, last-sync timestamp,
 * and four KPI tiles (total spend, utilization, cost per identity, potential savings).
 * Fetches live snapshot data via the SAP snapshot RTK Query endpoint and displays
 * trend indicators with color-coded utilization thresholds. When the API fails or
 * returns no data, displays dashes as fallback values.
 *
 * @param {TitleSectionProps} props - Component props
 * @param {string} [props.vendor='SAP'] - Vendor identifier used for the API fetch and display labels
 * @returns {React.ReactElement} Header section with navigation breadcrumbs, license info, and KPI metrics
 */
function TitleSection({
  vendor = 'SAP',
}: TitleSectionProps): React.ReactElement {
  const history = useHistory();

  // Using local mock data instead of API call
  const snapshotData = LICENSE_INTELLIGENCE_SNAPSHOT_DATA.data;
  const isError = false;

  // When API fails or data is unavailable, show dashes instead of mock values
  const hasData = Boolean(snapshotData) && !isError;

  const totalSpend = useMemo(
    () => ({
      displayValue: hasData
        ? snapshotData.totalLicenseSpend.displayValue
        : TITLE_SECTION_CONSTANTS.DASH,
      trend:
        hasData && snapshotData.totalLicenseSpend.trend
          ? {
              percentage: snapshotData.totalLicenseSpend.trend.label,
              direction: snapshotData.totalLicenseSpend.trend.direction,
            }
          : null,
    }),
    [hasData, snapshotData]
  );

  const utilization = useMemo(
    () => ({
      value: hasData ? snapshotData.licenseUtilization.value : 0,
      displayValue: hasData
        ? snapshotData.licenseUtilization.displayValue
        : TITLE_SECTION_CONSTANTS.DASH,
      trend:
        hasData && snapshotData.licenseUtilization.trend
          ? {
              percentage: snapshotData.licenseUtilization.trend.label,
              direction: snapshotData.licenseUtilization.trend.direction,
            }
          : null,
    }),
    [hasData, snapshotData]
  );

  const costPerUser = useMemo(
    () => ({
      value: hasData ? snapshotData.costPerUser.value : null,
      displayValue: hasData
        ? snapshotData.costPerUser.displayValue
        : TITLE_SECTION_CONSTANTS.DASH,
      trend:
        hasData && snapshotData.costPerUser.trend
          ? {
              percentage: snapshotData.costPerUser.trend.label,
              direction: snapshotData.costPerUser.trend.direction,
            }
          : null,
    }),
    [hasData, snapshotData]
  );

  const potentialSavings = useMemo(
    () => ({
      displayValue: hasData
        ? snapshotData.potentialSavings.displayValue
        : TITLE_SECTION_CONSTANTS.DASH,
      hasSimulation: hasData
        ? (snapshotData.potentialSavings as { hasSimulation?: boolean })
            .hasSimulation ?? false
        : false,
      trend:
        hasData && snapshotData.potentialSavings.trend
          ? {
              percentage: snapshotData.potentialSavings.trend.label,
              direction: snapshotData.potentialSavings.trend.direction,
            }
          : null,
    }),
    [hasData, snapshotData]
  );

  const lastSyncDisplay = useMemo(
    () =>
      hasData && snapshotData.lastUpdated
        ? `Last data sync: ${new Date(snapshotData.lastUpdated).toLocaleString(
            'en-US',
            {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone: 'UTC',
            }
          )}`
        : '',
    [hasData, snapshotData]
  );

  const handleBackClick = (): void => {
    history.push(AAG_LICENSE_INTELLIGENCE_BASENAME);
  };

  return (
    <div className='LicenseManagement-TitleSection'>
      {/* Top Row: Breadcrumbs and Last Sync */}
      <div className='LicenseManagement-TitleSection-top'>
        <div className='LicenseManagement-TitleSection-breadcrumbs'>
          <span className='LicenseManagement-TitleSection-breadcrumbText LicenseManagement-TitleSection-breadcrumbText--primary'>
            {TITLE_SECTION_CONSTANTS.LICENSE_INTELLIGENCE}
          </span>
          <Icon
            kind='ChevronRight'
            size='small'
            className='LicenseManagement-TitleSection-breadcrumbSeparatorIcon'
          />
          <span className='LicenseManagement-TitleSection-breadcrumbText'>
            {vendor.toUpperCase()} {TITLE_SECTION_CONSTANTS.LICENSE}
          </span>
        </div>
        <div className='LicenseManagement-TitleSection-syncInfo'>
          <span className='LicenseManagement-TitleSection-syncLabel'>
            {lastSyncDisplay}
          </span>
        </div>
      </div>

      {/* Middle Row: License Info and KPIs */}
      <div className='LicenseManagement-TitleSection-middle'>
        <div className='LicenseManagement-TitleSection-backButton'>
          <Button
            onClick={handleBackClick}
            className='LicenseManagement-TitleSection-backBtn'
            aria-label='Go back'>
            <Icon
              kind='ChevronLeft'
              size='medium'
              className='LicenseManagement-TitleSection-backBtnIcon'
            />
          </Button>
        </div>

        <div className='LicenseManagement-TitleSection-licenseSection'>
          <div className='LicenseManagement-TitleSection-licenseHeader'>
            <div className='LicenseManagement-TitleSection-logo'>
              <ApplicationIcon logo={vendor} size='xLarge' />
            </div>
            <div className='LicenseManagement-TitleSection-licenseDetails'>
              <div className='LicenseManagement-TitleSection-licenseName'>
                {vendor} {TITLE_SECTION_CONSTANTS.LICENSE_SNAPSHOT}
              </div>
              <div className='LicenseManagement-TitleSection-licenseSubName'>
                SAP RISE FUE
              </div>
              <div className='LicenseManagement-TitleSection-endpointChips'>
                <Chip
                  kind='outlined'
                  label='SAP S4HANA Cloud Prod'
                  color='success'
                  size='small'
                />
                <Chip
                  kind='outlined'
                  label='SAP S4HANA Cloud QA'
                  color='success'
                  size='small'
                />
              </div>
            </div>
          </div>
        </div>

        {/* KPI Metrics */}
        <div className='LicenseManagement-TitleSection-kpis'>
          {/* AC-FE-SNAP-1: Total License Spend */}
          <div className='LicenseManagement-TitleSection-kpi LicenseManagement-TitleSection-kpi--bordered'>
            <div className='LicenseManagement-TitleSection-kpiLabel'>
              {TITLE_SECTION_CONSTANTS.TOTAL_LICENSE_SPEND}
            </div>
            <div className='LicenseManagement-TitleSection-kpiValue'>
              {totalSpend.displayValue}
            </div>
            <div className='LicenseManagement-TitleSection-kpiSub'>
              {totalSpend.trend && (
                <>
                  <span
                    className={`LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--trend-${totalSpend.trend.direction}`}>
                    {totalSpend.trend.percentage}{' '}
                    {TITLE_SECTION_CONSTANTS.YOY_LABEL}
                  </span>
                  <Icon
                    kind={
                      totalSpend.trend.direction === 'up'
                        ? 'TrendUp'
                        : 'TrendDown'
                    }
                    size='small'
                    className={`LicenseManagement-TitleSection-kpiTrend--${totalSpend.trend.direction}`}
                  />
                </>
              )}
            </div>
          </div>

          {/* AC-FE-SNAP-2: License Utilization */}
          <div className='LicenseManagement-TitleSection-kpi LicenseManagement-TitleSection-kpi--bordered'>
            <div className='LicenseManagement-TitleSection-kpiLabel'>
              {TITLE_SECTION_CONSTANTS.LICENSE_UTILIZATION}
            </div>
            <div
              className={`LicenseManagement-TitleSection-kpiValue LicenseManagement-TitleSection-kpiValue--utilization-${getUtilizationThreshold(
                utilization.value
              )}`}>
              {utilization.displayValue}
            </div>
            <div className='LicenseManagement-TitleSection-kpiSub'>
              {hasData && (
                <span className='LicenseManagement-TitleSection-kpiSubText'>
                  {TITLE_SECTION_CONSTANTS.LICENSE_UTILIZATION_SUB}
                </span>
              )}
              {utilization.trend && (
                <>
                  <span
                    className={`LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--trend-${utilization.trend.direction}`}>
                    {utilization.trend.percentage}
                  </span>
                  <Icon
                    kind={
                      utilization.trend.direction === 'up'
                        ? 'TrendUp'
                        : 'TrendDown'
                    }
                    size='small'
                    className={`LicenseManagement-TitleSection-kpiTrend--${utilization.trend.direction}`}
                  />
                </>
              )}
            </div>
          </div>

          {/* AC-FE-SNAP-3: Cost Per User */}
          <div className='LicenseManagement-TitleSection-kpi LicenseManagement-TitleSection-kpi--bordered'>
            <div className='LicenseManagement-TitleSection-kpiLabel'>
              {TITLE_SECTION_CONSTANTS.COST_PER_IDENTITY}
            </div>
            <div className='LicenseManagement-TitleSection-kpiValue'>
              {costPerUser.value !== null ? (
                costPerUser.displayValue
              ) : (
                <Tooltip
                  text={TITLE_SECTION_CONSTANTS.NULL_VALUE_TOOLTIP}
                  placement='top'
                  trigger={
                    <span className='LicenseManagement-TitleSection-kpiValueDash'>
                      {TITLE_SECTION_CONSTANTS.DASH}
                    </span>
                  }
                />
              )}
            </div>
            <div className='LicenseManagement-TitleSection-kpiSub'>
              {hasData && (
                <span className='LicenseManagement-TitleSection-kpiSubText'>
                  {TITLE_SECTION_CONSTANTS.COST_PER_USER_SUB}
                </span>
              )}
              {costPerUser.trend && (
                <>
                  <span
                    className={`LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--trend-${costPerUser.trend.direction}`}>
                    {costPerUser.trend.percentage}
                  </span>
                  <Icon
                    kind={
                      costPerUser.trend.direction === 'up'
                        ? 'TrendUp'
                        : 'TrendDown'
                    }
                    size='small'
                    className={`LicenseManagement-TitleSection-kpiTrend--${costPerUser.trend.direction}`}
                  />
                </>
              )}
            </div>
          </div>

          {/* AC-FE-SNAP-4: Potential Savings */}
          <div className='LicenseManagement-TitleSection-kpi LicenseManagement-TitleSection-kpi--savings'>
            <div className='LicenseManagement-TitleSection-kpiLabel'>
              {TITLE_SECTION_CONSTANTS.POTENTIAL_SAVINGS}
            </div>
            <div className='LicenseManagement-TitleSection-kpiValue LicenseManagement-TitleSection-kpiValue--highlight'>
              {potentialSavings.hasSimulation
                ? potentialSavings.displayValue
                : TITLE_SECTION_CONSTANTS.DASH}
            </div>
            <div className='LicenseManagement-TitleSection-kpiSub'>
              {potentialSavings.hasSimulation ? (
                <>
                  {hasData && (
                    <span className='LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--highlight'>
                      {TITLE_SECTION_CONSTANTS.POTENTIAL_SAVINGS_SUB}
                    </span>
                  )}
                  {potentialSavings.trend && (
                    <>
                      <span
                        className={`LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--savings-${potentialSavings.trend.direction}`}>
                        {potentialSavings.trend.percentage}
                      </span>
                      <Icon
                        kind={
                          potentialSavings.trend.direction === 'up'
                            ? 'TrendUp'
                            : 'TrendDown'
                        }
                        size='small'
                        className={`LicenseManagement-TitleSection-kpiSavingsTrend--${potentialSavings.trend.direction}`}
                      />
                    </>
                  )}
                </>
              ) : (
                <span className='LicenseManagement-TitleSection-kpiSubText LicenseManagement-TitleSection-kpiSubText--muted'>
                  {TITLE_SECTION_CONSTANTS.NO_SIMULATION_SUBTITLE}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TitleSection;

TitleSection.defaultProps = {
  vendor: 'SAP',
};
